package com.fantasytenis.web.components

import androidx.compose.runtime.Composable
import com.fantasytenis.web.theme.Colors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Avatar display sizes.
 */
enum class AvatarSize(val sizePx: Int, val fontSizePx: Int) {
    XSMALL(24, 10),
    SMALL(32, 12),
    MEDIUM(48, 16),
    LARGE(80, 24),
    XLARGE(120, 36)
}

/**
 * Displays a user avatar with fallback to initials.
 *
 * Display priority:
 * 1. Custom avatar URL -> Show image
 * 2. Preset avatar URL -> Show image
 * 3. Initials -> Show initials in colored circle
 */
@Composable
fun AvatarDisplay(
    avatarUrl: String?,
    initials: String,
    size: AvatarSize = AvatarSize.MEDIUM,
    onClick: (() -> Unit)? = null
) {
    val isClickable = onClick != null

    val containerStyle: StyleScope.() -> Unit = {
        width(size.sizePx.px)
        height(size.sizePx.px)
        borderRadius(50.percent)
        overflow("hidden")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        flexShrink(0)
        if (isClickable) {
            cursor("pointer")
            property("transition", "transform 0.2s ease, box-shadow 0.2s ease")
        }
    }

    if (avatarUrl != null && avatarUrl.isNotBlank()) {
        // Show avatar image
        Div({
            style(containerStyle)
            if (isClickable) {
                classes("avatar-clickable")
            }
            if (onClick != null) {
                onClick { onClick() }
            }
        }) {
            Img(
                src = avatarUrl,
                alt = "Avatar",
                attrs = {
                    style {
                        width(100.percent)
                        height(100.percent)
                        property("object-fit", "cover")
                    }
                }
            )
        }
    } else {
        // Show initials fallback
        Div({
            style {
                containerStyle()
                backgroundColor(getAvatarColor(initials))
            }
            if (isClickable) {
                classes("avatar-clickable")
            }
            if (onClick != null) {
                onClick { onClick() }
            }
        }) {
            Span({
                style {
                    color(Color.white)
                    fontSize(size.fontSizePx.px)
                    fontWeight(600)
                    property("text-transform", "uppercase")
                    property("user-select", "none")
                }
            }) {
                Text(initials.take(2))
            }
        }
    }
}

/**
 * Generate a consistent color for initials based on the initials string.
 */
private fun getAvatarColor(initials: String): CSSColorValue {
    val colors = listOf(
        Color("#4CAF50"), // Green
        Color("#2196F3"), // Blue
        Color("#9C27B0"), // Purple
        Color("#FF5722"), // Deep Orange
        Color("#00BCD4"), // Cyan
        Color("#E91E63"), // Pink
        Color("#FF9800"), // Orange
        Color("#607D8B"), // Blue Grey
        Color("#3F51B5"), // Indigo
        Color("#009688")  // Teal
    )
    val hash = initials.hashCode()
    return colors[kotlin.math.abs(hash) % colors.size]
}

/**
 * Displays a user row with avatar and name.
 * Used in leaderboards and lists.
 */
@Composable
fun UserDisplayRow(
    avatarUrl: String?,
    displayName: String,
    initials: String,
    subtitle: String? = null,
    isCurrentUser: Boolean = false,
    avatarSize: AvatarSize = AvatarSize.SMALL,
    onClick: (() -> Unit)? = null
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
            if (onClick != null) {
                cursor("pointer")
            }
        }
        if (onClick != null) {
            onClick { onClick() }
        }
    }) {
        AvatarDisplay(
            avatarUrl = avatarUrl,
            initials = initials,
            size = avatarSize
        )

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(2.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(6.px)
                }
            }) {
                Span({
                    style {
                        fontWeight(500)
                        fontSize(14.px)
                        color(Colors.TextPrimary)
                    }
                }) {
                    Text(displayName)
                }
                if (isCurrentUser) {
                    Span({
                        style {
                            fontSize(12.px)
                            color(Colors.Accent)
                            fontWeight(500)
                        }
                    }) {
                        Text("(You)")
                    }
                }
            }
            subtitle?.let { sub ->
                Span({
                    style {
                        fontSize(12.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text(sub)
                }
            }
        }
    }
}
